import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Button } from '@zillow/constellation';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ConnectedNetworkView from 'app/shared/components/ConnectedNetworkView';
import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import { AppSelectors, PropertiesSelectors } from 'app/shared/selectors';
import { StyledTextLink, StyledText } from './StyledComponents';

const { track } = analyticsUtils;
const { GA_EVENTS } = AnalyticsConstants;
const { PAID_INCLUSION_VALUE_PROP_MODAL } = ModalConstants;
const { SYNDICATION_OVERVIEW_STATUS } = PropertyConstants;
const {
    makeIsScreenConstellationMobile,
    makeIsScreenConstellationLG,
} = AppSelectors;

// eslint-disable-next-line max-len
const listingDeclinedReasonsPage = 'https://help.zillowrentalmanager.com/hc/en-us/articles/217482388-Why-was-my-listing-Declined-';

const propTypes = {
    isInPaidMarket: PropTypes.bool.isRequired,
    syndication: PropTypes.object.isRequired,
};

const LearnMore = ({ isInPaidMarket, syndication }) => {
    const dispatch = useDispatch();
    const {
        leadGuarantee: {
            isEligible: isEligibleForLeadGuarantee,
        },
    } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const isScreenMobile = useSelector(state => makeIsScreenConstellationMobile(state));
    const isScreenLg = useSelector(state => makeIsScreenConstellationLG(state));
    const { overview: { status: syndicationOverviewStatus } } = syndication;

    const handlePaidInclusionLearnMoreClick = () => {
        dispatch(
            ModalActions.showModal({
                modalType: PAID_INCLUSION_VALUE_PROP_MODAL,
                className: 'PaidInclusionValuePropModal-container',
                useModalDialog: true,
                shouldCloseOnOverlayClick: true,
                isEligibleForLeadGuarantee,
                onOpen: () => dispatch(track.GAEventV2(GA_EVENTS.VALUE_PROP_VIEW)),
                footer: () => (
                    <Button
                        buttonType="primary"
                        size="md"
                        onClick={() => dispatch(ModalActions.hideModal())}
                    >
                        Done
                    </Button>
                ),
                onClose: () => dispatch(track.GAEventV2(GA_EVENTS.VALUE_PROP_DISMISS)),
            })
        );
        return false;
    };

    const renderPublishText = () => {
        const viewKey = isEligibleForLeadGuarantee ? 'leadGuarantee' : 'nonLeadGuaranteePaidInclusion';
        let sizeKey = 'xl';

        if (isScreenMobile) {
            sizeKey = 'sm';
        } else if (isScreenLg) {
            sizeKey = 'lg';
        }

        if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHED) {
            return 'View on Zillow Rental Network';
        }

        if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHING) {
            return `Published to ${syndication.overview.publishedCount} of 3 sites...`;
        }

        if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.DECLINED) {
            return (
                <StyledTextLink href={listingDeclinedReasonsPage} target="_blank">
                    Learn why it was declined
                </StyledTextLink>
            );
        }

        if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PARTIALLY_PUBLISHED) {
            return <StyledTextLink href={listingDeclinedReasonsPage} target="_blank">Learn why</StyledTextLink>;
        }

        if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.INACTIVE && !isInPaidMarket) {
            return <StyledText>Publish to the Zillow Rental Network</StyledText>;
        }

        const screeWidthTextMappings = {
            leadGuarantee: {
                sm: 'Incl. Lead Guarantee',
                lg: 'Includes Lead Guarantee',
                xl: 'Publish this listing with Lead Guarantee',
            },
            nonLeadGuaranteePaidInclusion: {
                sm: 'Learn more',
                lg: 'Learn about Zillow Rental Network',
                xl: 'Learn about Zillow Rental Network',
            },
        };
        return (
            <StyledTextLink onClick={handlePaidInclusionLearnMoreClick}>
                {screeWidthTextMappings[viewKey][sizeKey]}
            </StyledTextLink>
        );
    };

    return (
        <>
            {(
                syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHED ||
                syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHING) ? (
                    <ConnectedNetworkView syndication={syndication} label={renderPublishText()} />
                ) : (
                    <>
                        {renderPublishText()}
                    </>
                )}
        </>
    );
};

LearnMore.propTypes = propTypes;

export default LearnMore;
